import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * A right-hand slide-over drawer — the calm, focused reader for one item's full
 * detail (long markdown body + its decision controls), so the lists behind it can
 * stay scannable one-line-per-row. Closes on Esc or backdrop click. Optional footer
 * pins the primary actions to the bottom, out of the scroll.
 */
export function SidePanel({
  title,
  subtitle,
  onClose,
  footer,
  children,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div
        className={cn(
          'relative flex h-full w-full max-w-xl flex-col border-l border-border bg-surface pb-[env(safe-area-inset-bottom)] shadow-2xl',
          'animate-[slidein_180ms_ease-out]',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h3 className="truncate font-semibold text-fg">{title}</h3>
            {subtitle && <div className="mt-0.5 text-sm text-fg-muted">{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-lg p-1 text-fg-subtle hover:bg-surface-2 hover:text-fg"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4">{children}</div>
        {footer && <div className="border-t border-border px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}
